import React from 'react';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

export const RELATION_OPTIONS = ['self', 'wife', 'husband', 'son', 'daughter', 'father', 'mother', 'sibling', 'other'];
export const OCCASION_TYPES = ['birthday', 'marriage_anniversary', 'other'];
export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Month select label, e.g. "January (1)" */
export function formatMonthSelectLabel(month) {
  const mo = Number(month);
  if (!Number.isInteger(mo) || mo < 1 || mo > 12) return String(month ?? '');
  return `${MONTH_NAMES[mo - 1]} (${mo})`;
}

/** Use with Dialog onClose so only CloseX / explicit buttons dismiss (not backdrop or Escape). */
export function ignoreBackdropEscapeClose(_event, reason) {
  return reason === 'backdropClick' || reason === 'escapeKeyDown';
}

export function formatRelationLabel(value) {
  if (!value) return '—';
  const v = String(value).toLowerCase();
  const map = {
    self: 'Self',
    spouse: 'Spouse',
    wife: 'Wife',
    husband: 'Husband',
    son: 'Son',
    daughter: 'Daughter',
    father: 'Father',
    mother: 'Mother',
    sibling: 'Sibling',
    other: 'Other',
  };
  return map[v] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}

export function formatOccasionTypeLabel(type, customLabel) {
  if (!type) return customLabel || '—';
  const t = String(type).toLowerCase();
  if (t === 'other' && customLabel && String(customLabel).trim()) return String(customLabel).trim();
  const map = { birthday: 'Birthday', marriage_anniversary: 'Marriage anniversary', other: 'Other' };
  return map[t] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}

/** Month (1–12) and day (1–31) → e.g. "Oct 19" */
export function formatOccasionMonthDay(month, day) {
  const mo = Number(month);
  const da = Number(day);
  if (!Number.isInteger(mo) || mo < 1 || mo > 12) return '—';
  if (!Number.isInteger(da) || da < 1 || da > 31) return '—';
  const short = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${short[mo - 1]} ${da}`;
}

export const DetailRow = ({ label, value, editable = true, onEdit }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mr: 2, minWidth: 140 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
    </Box>
    {editable && (
      <Button size="small" variant="outlined" onClick={onEdit} sx={{ ml: 2 }}>
        Edit
      </Button>
    )}
  </Box>
);

export const CloseX = ({ onClick, title = 'Close', disabled = false }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    title={title}
    role="button"
    aria-label="Close"
    aria-disabled={disabled}
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      backgroundColor: '#e53935',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      '&:hover': { filter: disabled ? undefined : 'brightness(0.95)' },
    }}
  >
    {CloseIcon ? <CloseIcon sx={{ fontSize: 18, color: '#fff' }} /> : 'X'}
  </Box>
);

export const FilterButton = ({ active, children, onClick }) => (
  <Button
    size="small"
    variant={active ? 'contained' : 'outlined'}
    color={active ? 'primary' : 'inherit'}
    onClick={onClick}
  >
    {children}
  </Button>
);

export const Stat = ({ label, value, help, sx }) => (
  <Card variant="outlined" sx={{ height: '100%', width: '100%', ...sx }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
      {help && (
        <Typography variant="caption" color="text.secondary">{help}</Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Shared MUI Table styling for portal data lists (Pledges, Donations, Statements, …).
 * Use with TableContainer component={Paper} variant="outlined" sx={portalTableContainerSx}.
 */
export const portalTableContainerSx = { borderRadius: 2, overflow: 'auto' };
export const portalTableHeadCellSx = { fontWeight: 700, bgcolor: 'grey.100' };

/** Alternating row background + hover; pass spread onto TableRow: sx={portalTableBodyRowSx(i)} */
export function portalTableBodyRowSx(rowIndex) {
  return {
    bgcolor: rowIndex % 2 === 0 ? 'action.hover' : 'background.paper',
  };
}
